import { markRaw } from 'vue'
import TrustedContactWorkerColumn from './TrustedContactWorkerColumn.vue'
import TrustedContactSkillsColumn from './TrustedContactSkillsColumn.vue'
import TrustedContactSkillsColumnHeader from './TrustedContactSkillsColumnHeader.vue'
import TrustedContactJobsCountColumn from './TrustedContactJobsCountColumn.vue'
import TrustedContactLocationColumn from './TrustedContactLocationColumn.vue'
import TrustedContactBusinessSetupColumn from './TrustedContactBusinessSetupColumn.vue'
import TrustedContactNotesColumn from './TrustedContactNotesColumn.vue'
import TrustedContactLinksColumn from './TrustedContactLinksColumn.vue'
import TrustedContactOriginatingCompanyColumn from './TrustedContactOriginatingCompanyColumn.vue'
import TrustedContactInvitedOnColumn from './TrustedContactInvitedOnColumn.vue'
import TrustedContactWorkerTypeColumn from './TrustedContactWorkerTypeColumn.vue'
import { SortOption } from '@/components/BaseDataTable/composables/dataTableSort'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import {
  TrustedContactWorkerColumnFragmentDoc,
  TrustedContactWorkerColumnCompactFragmentDoc,
  TrustedContactSkillsColumnFragmentDoc,
  TrustedContactJobsCountColumnFragmentDoc,
  TrustedContactLocationColumnFragmentDoc,
  TrustedContactBusinessSetupColumnFragmentDoc,
  TrustedContactLinksColumnFragmentDoc,
  TrustedContactNotesColumnFragmentDoc,
  TrustedContactOriginatingCompanyColumnFragmentDoc,
  TrustedContactInvitedOnColumnFragmentDoc,
  TrustedContactWorkerTypeColumnFragmentDoc,
  SortOrder,
  TrustedContactOrderByColumn,
  TrustedContactWorkerColumnFragment,
  TrustedContactWorkerColumnCompactFragment,
  TrustedContactSkillsColumnFragment,
  TrustedContactJobsCountColumnFragment,
  TrustedContactLocationColumnFragment,
  TrustedContactBusinessSetupColumnFragment,
  TrustedContactLinksColumnFragment,
  TrustedContactNotesColumnFragment,
  TrustedContactOriginatingCompanyColumnFragment,
  TrustedContactInvitedOnColumnFragment,
  TrustedContactWorkerTypeColumnFragment,
  TrustedContactManagedStatus
} from '@/gql/graphql'


const TrustedContactsColumns: ColumnMaps = {
  TrustedContactWorkerColumn: {
    fragment: TrustedContactWorkerColumnFragmentDoc,
    component: markRaw(TrustedContactWorkerColumn),
    columnDef: {
      title: $t('Name'),
      key: 'TrustedContactWorkerColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(TrustedContactWorkerColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: 'Worker Id',
        extraction: (row: TrustedContactWorkerColumnFragment) => `#${row.worker.id}`,
      },
      {
        title: 'Worker name',
        extraction: (row: TrustedContactWorkerColumnFragment) => row.worker.name,
      },
      {
        title: 'Worker title',
        extraction: (row: TrustedContactWorkerColumnFragment) => row.worker.jobTitle || '',
      },
    ],
    sort: [
      {
        name: 'WORKER_NAME_ASC',
        primaryText: $t('Name'),
        secondaryText: $t('A to Z'),
        orderByColumn: TrustedContactOrderByColumn.WorkerName,
        sortOrder: SortOrder.Asc,
      },
      {
        name: 'WORKER_NAME_DESC',
        primaryText: $t('Name'),
        secondaryText: $t('Z to A'),
        orderByColumn: TrustedContactOrderByColumn.WorkerName,
        sortOrder: SortOrder.Desc,
      },
      {
        name: 'WORKER_STATUS_UPDATED_DESC',
        primaryText: $t('Last status change'),
        secondaryText: null,
        orderByColumn: TrustedContactOrderByColumn.WorkerStatusUpdated,
        sortOrder: SortOrder.Desc,
      },
    ],
  },
  // Same Vue component as TrustedContactWorkerColumn, registered with a slim
  // fragment that omits the contact-status and currently-hired labels. Use this
  // on surfaces (e.g. compliance tracker) where those statuses are conveyed by
  // a dedicated column or are otherwise off-topic for the table.
  TrustedContactWorkerColumnCompact: {
    fragment: TrustedContactWorkerColumnCompactFragmentDoc,
    component: markRaw(TrustedContactWorkerColumn),
    columnDef: {
      title: $t('Name'),
      key: 'TrustedContactWorkerColumnCompact',
      orderAble: true,
      renderUsing: {
        component: markRaw(TrustedContactWorkerColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: 'Worker Id',
        extraction: (row: TrustedContactWorkerColumnCompactFragment) => `#${row.worker.id}`,
      },
      {
        title: 'Worker name',
        extraction: (row: TrustedContactWorkerColumnCompactFragment) => row.worker.name,
      },
      {
        title: 'Worker title',
        extraction: (row: TrustedContactWorkerColumnCompactFragment) => row.worker.jobTitle || '',
      },
    ],
    sort: [
      {
        name: 'WORKER_NAME_ASC',
        primaryText: $t('Name'),
        secondaryText: $t('A to Z'),
        orderByColumn: TrustedContactOrderByColumn.WorkerName,
        sortOrder: SortOrder.Asc,
      },
      {
        name: 'WORKER_NAME_DESC',
        primaryText: $t('Name'),
        secondaryText: $t('Z to A'),
        orderByColumn: TrustedContactOrderByColumn.WorkerName,
        sortOrder: SortOrder.Desc,
      },
    ],
  },
  TrustedContactSkillsColumn: {
    fragment: TrustedContactSkillsColumnFragmentDoc,
    component: markRaw(TrustedContactSkillsColumn),
    columnDef: {
      title: $t('Skills'),
      key: 'TrustedContactSkillsColumn',
      slot: 'TrustedContactSkillsColumn',
      orderAble: false,
      renderHeadUsing: {
        component: markRaw(TrustedContactSkillsColumnHeader),
      },
      renderUsing: {
        component: markRaw(TrustedContactSkillsColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: 'Company skills',
        extraction: (row: TrustedContactSkillsColumnFragment) =>
          row.skills.map((s) => s.name).join(', '),
      },
      {
        title: 'Worker skills',
        extraction: (row: TrustedContactSkillsColumnFragment) =>
          row.worker.skills.map((s) => s.name).join(', '),
      },
    ],
  },
  TrustedContactJobsCountColumn: {
    fragment: TrustedContactJobsCountColumnFragmentDoc,
    component: markRaw(TrustedContactJobsCountColumn),
    columnDef: {
      title: $t('Total hires'),
      key: 'TrustedContactJobsCountColumn',
      orderAble: true,
      colClass: { 'tw-whitespace-nowrap': true },
      renderUsing: {
        component: markRaw(TrustedContactJobsCountColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: 'Total hires',
        extraction: (row: TrustedContactJobsCountColumnFragment) =>
          `${row.acceptedHiresCount}`,
      },
    ],
    sort: [
      {
        name: 'WORKER_JOBS_COUNT_DESC',
        primaryText: $t('Total Hires'),
        secondaryText: $t('More to less'),
        orderByColumn: TrustedContactOrderByColumn.WorkerJobsCount,
        sortOrder: SortOrder.Desc,
      },
    ],
  },
  TrustedContactLocationColumn: {
    fragment: TrustedContactLocationColumnFragmentDoc,
    component: markRaw(TrustedContactLocationColumn),
    columnDef: {
      title: $t('Location'),
      key: 'TrustedContactLocationColumn',
      class: 'vmiddle tw-cursor-pointer tw-text-gray-850 tw-text-[13px]',
      renderUsing: {
        component: markRaw(TrustedContactLocationColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: 'City',
        extraction: (row: TrustedContactLocationColumnFragment) => row.worker.address?.city || '',
      },
      {
        title: 'State',
        extraction: (row: TrustedContactLocationColumnFragment) => row.worker.address?.state || '',
      },
      {
        title: 'Country',
        extraction: (row: TrustedContactLocationColumnFragment) =>
          row.worker.address?.country?.name || '',
      },
      {
        title: 'Country code',
        extraction: (row: TrustedContactLocationColumnFragment) =>
          row.worker.address?.country?.isoCode || '',
      },
      {
        title: 'Location',
        extraction: (row: TrustedContactLocationColumnFragment) => {
          const a = row.worker.address
          if (!a) return ''
          return [a.city, a.state, a.country?.name].filter(Boolean).join(', ')
        },
      },
    ],
  },
  TrustedContactBusinessSetupColumn: {
    fragment: TrustedContactBusinessSetupColumnFragmentDoc,
    component: markRaw(TrustedContactBusinessSetupColumn),
    columnDef: {
      title: $t('Business setup'),
      key: 'TrustedContactBusinessSetupColumn',
      colClass: { 'tw-whitespace-nowrap': true },
      renderUsing: {
        component: markRaw(TrustedContactBusinessSetupColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: 'Business setup',
        extraction: (row: TrustedContactBusinessSetupColumnFragment) =>
          row.worker.primaryBusinessEntity?.name || '-',
      },
    ],
  },
  TrustedContactLinksColumn: {
    fragment: TrustedContactLinksColumnFragmentDoc,
    component: markRaw(TrustedContactLinksColumn),
    columnDef: {
      title: $t('Links'),
      key: 'TrustedContactLinksColumn',
      orderAble: false,
      colClass: { 'tw-whitespace-nowrap': true },
      renderUsing: {
        component: markRaw(TrustedContactLinksColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: 'Links',
        extraction: (row: TrustedContactLinksColumnFragment) => row.worker.profile?.links.map((link) => link.url).join(', ') ?? '',
      },
    ],
  },
  TrustedContactNotesColumn: {
    fragment: TrustedContactNotesColumnFragmentDoc,
    component: markRaw(TrustedContactNotesColumn),
    columnDef: {
      title: $t('Notes'),
      key: 'TrustedContactNotesColumn',
      ignoreClickForRowSelection: true,
      colClass: { 'tw-whitespace-nowrap': true },
      renderUsing: {
        component: markRaw(TrustedContactNotesColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Notes count',
        extraction: (row: TrustedContactNotesColumnFragment) => `${row.notes.paginatorInfo.total}`,
      },
    ],
  },
  TrustedContactOriginatingCompanyColumn: {
    fragment: TrustedContactOriginatingCompanyColumnFragmentDoc,
    component: markRaw(TrustedContactOriginatingCompanyColumn),
    columnDef: {
      title: $t('Originating company'),
      key: 'TrustedContactOriginatingCompanyColumn',
      orderAble: false,
      colClass: { 'tw-whitespace-nowrap': true },
      renderUsing: {
        component: markRaw(TrustedContactOriginatingCompanyColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Originating company',
        extraction: (row: TrustedContactOriginatingCompanyColumnFragment) =>
          row.company?.name || '',
      },
    ],
  },
  TrustedContactWorkerTypeColumn: {
    fragment: TrustedContactWorkerTypeColumnFragmentDoc,
    component: markRaw(TrustedContactWorkerTypeColumn),
    columnDef: {
      title: $t('Worker type'),
      key: 'TrustedContactWorkerTypeColumn',
      orderAble: false,
      colClass: { 'tw-whitespace-nowrap': true },
      renderUsing: {
        component: markRaw(TrustedContactWorkerTypeColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: 'Worker type',
        extraction: (row: TrustedContactWorkerTypeColumnFragment) => {
          switch (row.managedStatus) {
            case TrustedContactManagedStatus.StaffingAgency:
              return 'Managed'
            case TrustedContactManagedStatus.Unmanaged:
              return 'Unmanaged'
            default:
              return 'Worker account'
          }
        },
      },
    ],
  },
  TrustedContactInvitedOnColumn: {
    fragment: TrustedContactInvitedOnColumnFragmentDoc,
    component: markRaw(TrustedContactInvitedOnColumn),
    columnDef: {
      title: $t('Invited on'),
      key: 'TrustedContactInvitedOnColumn',
      orderAble: true,
      colClass: { 'tw-whitespace-nowrap': true },
      renderUsing: {
        component: markRaw(TrustedContactInvitedOnColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: 'Invited on',
        extraction: (row: TrustedContactInvitedOnColumnFragment) => row.invitedAt ?? '',
      },
    ],
    sort: [
      {
        name: 'WORKER_DATE_ADDED_DESC',
        primaryText: $t('Invited on'),
        secondaryText: $t('Most recent first'),
        orderByColumn: TrustedContactOrderByColumn.WorkerDateAdded,
        sortOrder: SortOrder.Desc,
      },
      {
        name: 'WORKER_DATE_ADDED_ASC',
        primaryText: $t('Invited on'),
        secondaryText: $t('Oldest first'),
        orderByColumn: TrustedContactOrderByColumn.WorkerDateAdded,
        sortOrder: SortOrder.Asc,
      },
    ],
  },
}

export const trustedContactsSortOptions: SortOption[] = [
  {
    name: 'WORKER_DATE_ADDED_DESC',
    primaryText: $t('Date added'),
    secondaryText: $t('New to old'),
    orderByColumn: TrustedContactOrderByColumn.WorkerDateAdded,
    sortOrder: SortOrder.Desc,
  },
  {
    name: 'WORKER_DATE_ADDED_ASC',
    primaryText: $t('Date added'),
    secondaryText: $t('Old to new'),
    orderByColumn: TrustedContactOrderByColumn.WorkerDateAdded,
    sortOrder: SortOrder.Asc,
  },
  {
    name: 'WORKER_NAME_ASC',
    primaryText: $t('Name'),
    secondaryText: $t('A to Z'),
    orderByColumn: TrustedContactOrderByColumn.WorkerName,
    sortOrder: SortOrder.Asc,
  },
  {
    name: 'WORKER_NAME_DESC',
    primaryText: $t('Name'),
    secondaryText: $t('Z to A'),
    orderByColumn: TrustedContactOrderByColumn.WorkerName,
    sortOrder: SortOrder.Desc,
  },
  {
    name: 'WORKER_STATUS_UPDATED_DESC',
    primaryText: $t('Last status change'),
    secondaryText: null,
    orderByColumn: TrustedContactOrderByColumn.WorkerStatusUpdated,
    sortOrder: SortOrder.Desc,
  },
  {
    name: 'WORKER_JOBS_COUNT_DESC',
    primaryText: $t('Total Hires'),
    secondaryText: $t('More to less'),
    orderByColumn: TrustedContactOrderByColumn.WorkerJobsCount,
    sortOrder: SortOrder.Desc,
  },
]

export const trustedContactsDefaultOrderBy = {
  field: TrustedContactOrderByColumn.WorkerDateAdded,
  order: SortOrder.Desc,
}

export default TrustedContactsColumns
